import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import axios from 'axios';
import { countries } from '../data/countries';

interface CreateClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DEFAULT_COUNTRY_CODE = 'CA';

const CreateClientDialog: React.FC<CreateClientDialogProps> = ({ open, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [twilioNumber, setTwilioNumber] = useState('');
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_CODE);
  const [subaccountName, setSubaccountName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone.trim());
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError('');
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTwilioNumber(value);
    setPhoneError('');
    if (value && !validatePhoneNumber(value)) {
      setPhoneError('Please enter a valid phone number with country code (e.g., 1234567890)');
    }
  };

  const handleCountryChange = (e: SelectChangeEvent<string>) => {
    setCountryIso(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!validatePhoneNumber(twilioNumber)) {
      setPhoneError('Please enter a valid phone number with country code');
      return;
    }

    setLoading(true);

    try {
      // Convert ISO code to callingCode for the API
      const country = countries.find(c => c.code === countryIso);
      const callingCode = country ? country.callingCode : '1';

      const response = await axios.post('/api/admin/clients', {
        email,
        twilioNumber,
        countryCode: callingCode,
        subaccountName: subaccountName || undefined
      });

      setSuccess(response.data.message || 'Client created successfully!');

      setTimeout(() => {
        setEmail('');
        setTwilioNumber('');
        setCountryIso(DEFAULT_COUNTRY_CODE);
        setSubaccountName('');
        setSuccess('');
        onSuccess();
        onClose();
      }, 2000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to create client. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setTwilioNumber('');
      setCountryIso(DEFAULT_COUNTRY_CODE);
      setSubaccountName('');
      setError('');
      setSuccess('');
      setEmailError('');
      setPhoneError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Client</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError || 'Client will receive login credentials at this email'}
            disabled={loading}
            autoFocus
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label="Phone Number"
            value={twilioNumber}
            onChange={handlePhoneChange}
            error={!!phoneError}
            helperText={phoneError || 'Include country code (e.g., 1234567890 or +1234567890)'}
            placeholder="1234567890"
            disabled={loading}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="country-select-label">Country</InputLabel>
            <Select
              labelId="country-select-label"
              id="country-select"
              value={countryIso}
              label="Country"
              onChange={handleCountryChange}
              disabled={loading}
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name} (+{country.callingCode})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="normal"
            required
            fullWidth
            label="Subaccount Name"
            value={subaccountName}
            onChange={(e) => setSubaccountName(e.target.value)}
            helperText="Required — use format: ClientName-Region (e.g., AcmeCorp-US)"
            disabled={loading}
            error={!subaccountName.trim() && !!email}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !!emailError || !!phoneError || !email || !twilioNumber || !subaccountName.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Client'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateClientDialog;
